// hooks/usePhoneVerification.ts
import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../api/apiService';
import { API_ENDPOINTS } from '../api/config';

const OTP_COUNTDOWN_SECONDS = 600; // 10 minutes

export interface PhoneVerificationState {
  phoneNumber: string | null;
  isRequesting: boolean;
  isConfirming: boolean;
  isVerified: boolean;
  countdown: number;
  error: string | null;
}

export interface UsePhoneVerificationReturn {
  // State
  phoneNumber: string | null;
  isRequesting: boolean;
  isConfirming: boolean;
  isVerified: boolean;
  countdown: number;
  error: string | null;
  canResend: boolean;
  botCode: string | null;
  botCountdown: number;
  botStatus: 'idle' | 'pending' | 'associated' | 'failed';
  // Actions
  requestOtp: (phoneNumber: string) => Promise<boolean>;
  confirmOtp: (code: string) => Promise<boolean>;
  startBotFlow: (code: string, expiresInSeconds?: number) => void;
  markBotAssociated: () => void;
  failBotFlow: (message?: string) => void;
  resetVerification: () => void;
  clearError: () => void;
}

export const usePhoneVerification = (): UsePhoneVerificationReturn => {
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [botCode, setBotCode] = useState<string | null>(null);
  const [botCountdown, setBotCountdown] = useState(0);
  const [botStatus, setBotStatus] = useState<'idle' | 'pending' | 'associated' | 'failed'>('idle');
  
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const botCountdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownStartRef = useRef<number | null>(null);

  // Clear countdown interval on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (botCountdownIntervalRef.current) {
        clearInterval(botCountdownIntervalRef.current);
      }
    };
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (countdown <= 0) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Initialize countdown start time if not set
    if (countdownStartRef.current === null) {
      countdownStartRef.current = Date.now();
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          countdownStartRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [countdown]);

  useEffect(() => {
    if (botCountdown <= 0) {
      if (botCountdownIntervalRef.current) {
        clearInterval(botCountdownIntervalRef.current);
        botCountdownIntervalRef.current = null;
      }
      return;
    }

    botCountdownIntervalRef.current = setInterval(() => {
      setBotCountdown((prev) => {
        if (prev <= 1) {
          if (botCountdownIntervalRef.current) {
            clearInterval(botCountdownIntervalRef.current);
            botCountdownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (botCountdownIntervalRef.current) {
        clearInterval(botCountdownIntervalRef.current);
        botCountdownIntervalRef.current = null;
      }
    };
  }, [botCountdown]);

  const startCountdown = useCallback(() => {
    setCountdown(OTP_COUNTDOWN_SECONDS);
    countdownStartRef.current = Date.now();
  }, []);

  const startBotFlow = useCallback((code: string, expiresInSeconds = OTP_COUNTDOWN_SECONDS) => {
    setBotCode(code);
    setBotCountdown(expiresInSeconds);
    setBotStatus('pending');
    setError(null);
  }, []);

  const markBotAssociated = useCallback(() => {
    setBotStatus('associated');
    setBotCountdown(0);
  }, []);

  const failBotFlow = useCallback((message = 'Bot verification failed.') => {
    setBotStatus('failed');
    setError(message);
  }, []);

  const requestOtp = useCallback(async (phone: string): Promise<boolean> => {
    setIsRequesting(true);
    setError(null);

    try {
      const response = await apiFetch<{ detail?: string; message?: string }>(
        API_ENDPOINTS.PHONE_REQUEST_OTP,
        {
          method: 'POST',
          body: JSON.stringify({ phone_number: phone }),
        }
      );

      if (response.ok) {
        setPhoneNumber(phone);
        startCountdown();
        return true;
      } else {
        const errorMessage = response.data?.detail 
          || response.data?.message 
          || 'Failed to send verification code';
        setError(errorMessage);
        return false;
      }
    } catch (err) {
      console.error('Error requesting OTP:', err);
      setError('Network error. Please try again.');
      return false;
    } finally {
      setIsRequesting(false);
    }
  }, [startCountdown]);

  const confirmOtp = useCallback(async (code: string): Promise<boolean> => {
    if (!phoneNumber) {
      setError('Please request a verification code first');
      return false;
    }

    setIsConfirming(true);
    setError(null);

    try {
      const response = await apiFetch<{ detail?: string; verified?: boolean; token?: string }>(
        API_ENDPOINTS.PHONE_CONFIRM_OTP,
        {
          method: 'POST',
          body: JSON.stringify({ 
            phone_number: phoneNumber,
            code: code,
          }),
        }
      );

      if (response.ok) {
        setIsVerified(true);
        setCountdown(0);
        return true;
      } else {
        const errorMessage = response.data?.detail || 'Invalid or expired verification code';
        setError(errorMessage);
        return false;
      }
    } catch (err) {
      console.error('Error confirming OTP:', err);
      setError('Network error. Please try again.');
      return false;
    } finally {
      setIsConfirming(false);
    }
  }, [phoneNumber]);

  const resetVerification = useCallback(() => {
    setPhoneNumber(null);
    setIsVerified(false);
    setCountdown(0);
    setBotCode(null);
    setBotCountdown(0);
    setBotStatus('idle');
    setError(null);
    countdownStartRef.current = null;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    phoneNumber,
    isRequesting,
    isConfirming,
    isVerified,
    countdown,
    error,
    canResend: countdown === 0,
    botCode,
    botCountdown,
    botStatus,
    requestOtp,
    confirmOtp,
    startBotFlow,
    markBotAssociated,
    failBotFlow,
    resetVerification,
    clearError,
  };
};
